import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  MenuToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  Select,
  SelectList,
  SelectOption,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, SearchIcon, TimesIcon, UserIcon, UsersIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useImpersonationTargets } from '../../../hooks/useImpersonationTargets';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import { useImpersonationWithNotifications } from '../../../contexts/impersonation/useImpersonationWithNotifications';
import { ImpersonationTarget } from '../../../types/models/ImpersonationTarget';

// A target is "impersonatable" only if it has a non-empty netbox_owner_name.
// Targets without one cannot be successfully impersonated (the backend's NetBox
// scoping requires the owner name), so we surface this both as a banner and as
// per-row disabled buttons. This data-quality issue is a hint for Ancalagon
// staff to fix the underlying customer record.
const isImpersonatable = (t: ImpersonationTarget): boolean =>
  t.netbox_owner_name !== null && t.netbox_owner_name.trim() !== '';

type ImpersonatabilityFilter = 'all' | 'impersonatable' | 'broken';

const IMPERSONATABILITY_FILTER_STORAGE_KEY = 'ancalagon.customers.impersonatabilityFilter';

const isValidImpersonatabilityFilter = (v: unknown): v is ImpersonatabilityFilter =>
  v === 'all' || v === 'impersonatable' || v === 'broken';

const impersonatabilityFilterLabel: Record<ImpersonatabilityFilter, string> = {
  all: 'All organizations',
  impersonatable: 'Can be impersonated',
  broken: 'Cannot be impersonated',
};

// localStorage access can throw in private/quota-restricted contexts; on read
// failure fall back to "all" so the page still renders.
const readPersistedImpersonatabilityFilter = (): ImpersonatabilityFilter => {
  try {
    const v = localStorage.getItem(IMPERSONATABILITY_FILTER_STORAGE_KEY);
    return isValidImpersonatabilityFilter(v) ? v : 'all';
  } catch {
    return 'all';
  }
};

function Customers() {
  const auth = useAuth();
  const { targets, loading, error, refetch } = useImpersonationTargets();
  const { target: activeTarget, isImpersonating } = useImpersonation();
  const { startWithNotification, stopWithNotification } = useImpersonationWithNotifications();

  const [confirmTarget, setConfirmTarget] = useState<ImpersonationTarget | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [impersonatabilityFilter, setImpersonatabilityFilter] = useState<ImpersonatabilityFilter>(
    readPersistedImpersonatabilityFilter,
  );
  const [isImpersonatabilityFilterOpen, setIsImpersonatabilityFilterOpen] = useState(false);

  const onImpersonatabilityFilterSelect = (
    _event: React.MouseEvent<Element, MouseEvent> | undefined,
    value: string | number | undefined,
  ) => {
    if (isValidImpersonatabilityFilter(value)) {
      setImpersonatabilityFilter(value);
    }
    setIsImpersonatabilityFilterOpen(false);
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  // Persist the impersonatability filter across page loads. Best-effort:
  // localStorage writes can throw in private/quota-restricted contexts, in
  // which case the filter still works for the current session.
  useEffect(() => {
    try {
      localStorage.setItem(IMPERSONATABILITY_FILTER_STORAGE_KEY, impersonatabilityFilter);
    } catch {
      // Ignore.
    }
  }, [impersonatabilityFilter]);

  // Reset to page 1 whenever the impersonatability filter changes — mirrors
  // the existing search-debounce reset, otherwise the user can land on a now
  // out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [impersonatabilityFilter]);

  const filteredTargets = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return targets.filter((t) => {
      if (impersonatabilityFilter === 'impersonatable' && !isImpersonatable(t)) return false;
      if (impersonatabilityFilter === 'broken' && isImpersonatable(t)) return false;
      if (q) {
        const nameMatches = t.name.toLowerCase().includes(q);
        const ownerMatches = t.netbox_owner_name?.toLowerCase().includes(q) ?? false;
        if (!nameMatches && !ownerMatches) return false;
      }
      return true;
    });
  }, [targets, debouncedSearch, impersonatabilityFilter]);

  // Count of broken targets across the FULL list (not just current page) — the
  // banner reports a system-wide number, not a per-page one.
  const brokenCount = useMemo(() => targets.filter((t) => !isImpersonatable(t)).length, [targets]);

  const paginatedTargets = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredTargets.slice(start, start + perPage);
  }, [filteredTargets, page, perPage]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const handleConfirmImpersonate = () => {
    if (!confirmTarget) return;
    startWithNotification(confirmTarget);
    setConfirmTarget(null);
  };

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load organizations"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error.message}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (targets.length === 0) {
      return (
        <EmptyState titleText="No organizations to switch to" icon={UsersIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            <Content>
              <p>
                You currently have no impersonation grants. This means there are no other organizations whose data you
                can view or manage from this account.
              </p>
              <p>
                <em>
                  In a future release, you will be able to grant a manager from another organization access to your own
                  — enabling cross-organization collaboration without sharing credentials. For now, contact an Ancalagon
                  administrator to request access to another organization.
                </em>
              </p>
            </Content>
          </EmptyStateBody>
        </EmptyState>
      );
    }

    return (
      <>
        {brokenCount > 0 && (
          <Alert
            variant="warning"
            isInline
            title={
              brokenCount === 1
                ? '1 organization cannot be impersonated'
                : `${brokenCount} organizations cannot be impersonated`
            }
            className="pf-v6-u-mb-md"
          >
            {brokenCount === 1
              ? '1 organization is missing its NetBox owner name and cannot be impersonated until this is fixed. It appears in the list below with a disabled action.'
              : `${brokenCount} organizations are missing their NetBox owner name and cannot be impersonated until this is fixed. They appear in the list below with a disabled action.`}
          </Alert>
        )}

        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by name or NetBox owner..."
                value={searchValue}
                onChange={(_event, value) => setSearchValue(value)}
                onClear={() => setSearchValue('')}
              />
            </ToolbarItem>
            <ToolbarItem>
              <Select
                isOpen={isImpersonatabilityFilterOpen}
                selected={impersonatabilityFilter}
                onSelect={onImpersonatabilityFilterSelect}
                onOpenChange={(isOpen) => setIsImpersonatabilityFilterOpen(isOpen)}
                toggle={(toggleRef) => (
                  <MenuToggle
                    ref={toggleRef}
                    onClick={() => setIsImpersonatabilityFilterOpen(!isImpersonatabilityFilterOpen)}
                    isExpanded={isImpersonatabilityFilterOpen}
                    aria-label="Filter by impersonatability"
                  >
                    {impersonatabilityFilterLabel[impersonatabilityFilter]}
                  </MenuToggle>
                )}
              >
                <SelectList>
                  <SelectOption value="all">All organizations</SelectOption>
                  <SelectOption value="impersonatable">Can be impersonated</SelectOption>
                  <SelectOption value="broken">Cannot be impersonated</SelectOption>
                </SelectList>
              </Select>
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filteredTargets.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                onPerPageSelect={onPerPageSelect}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filteredTargets.length === 0 ? (
          <EmptyState titleText="No organizations match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Try a different search term, or clear the filter to see all organizations.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button
                  variant="primary"
                  onClick={() => {
                    setSearchValue('');
                    setImpersonatabilityFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Available organizations" variant="compact">
            <Thead>
              <Tr>
                <Th>Organization</Th>
                <Th>NetBox owner</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedTargets.map((t) => {
                const isActive = activeTarget?.id === t.id;
                const canImpersonate = isImpersonatable(t);
                return (
                  <Tr key={t.id}>
                    <Td dataLabel="Organization">
                      {t.name}
                      {isActive && (
                        <Label color="orange" className="pf-v6-u-ml-sm">
                          Active
                        </Label>
                      )}
                    </Td>
                    <Td dataLabel="NetBox owner">
                      {t.netbox_owner_name ?? (
                        <Content component="small" className="pf-v6-u-color-200">
                          —
                        </Content>
                      )}
                    </Td>
                    <Td dataLabel="Actions">
                      {isActive ? (
                        <Button variant="danger" size="sm" icon={<TimesIcon />} onClick={stopWithNotification}>
                          Stop impersonation
                        </Button>
                      ) : canImpersonate ? (
                        <Button variant="warning" size="sm" icon={<UserIcon />} onClick={() => setConfirmTarget(t)}>
                          Impersonate
                        </Button>
                      ) : (
                        <Tooltip content="This organization has no NetBox owner name set and cannot be impersonated.">
                          <Button variant="warning" size="sm" icon={<UserIcon />} isAriaDisabled>
                            Impersonate
                          </Button>
                        </Tooltip>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}

        {filteredTargets.length > 0 && (
          <Pagination
            itemCount={filteredTargets.length}
            perPage={perPage}
            page={page}
            onSetPage={onSetPage}
            onPerPageSelect={onPerPageSelect}
            variant="bottom"
          />
        )}
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Organization</BreadcrumbItem>
          <BreadcrumbItem isActive>Switch organization</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <UsersIcon />
          </Icon>
          Switch organization
        </Content>
        <Content component="p">
          {isImpersonating ? (
            <>
              You are currently impersonating another organization. The list below shows <strong>your own</strong>{' '}
              impersonation grants as the authenticated user — not the grants of the organization you are currently
              acting as. Pick another row to switch the active target directly, or use{' '}
              <strong>Stop impersonation</strong> on the active row to return to your own organization.
            </>
          ) : (
            <>
              View and manage another organization&apos;s data on their behalf. While you are impersonating, every
              action you take applies to the impersonated organization — not your own.
            </>
          )}
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>

      <Modal
        variant="small"
        isOpen={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        aria-label="Confirm impersonation"
      >
        <ModalHeader title="Impersonate organization" />
        <ModalBody>
          <Alert variant="warning" isInline title="Privileged action" className="pf-v6-u-mb-md">
            You are about to act on behalf of another organization. Until you stop impersonating, every page, every API
            request, and every action you take will apply to <strong>{confirmTarget?.name}</strong>&apos;s data, not
            your own.
          </Alert>
          <Content>
            Impersonate <strong>{confirmTarget?.name}</strong>
            {confirmTarget?.netbox_owner_name && (
              <span className="pf-v6-u-color-200"> ({confirmTarget.netbox_owner_name})</span>
            )}
            ?
          </Content>
        </ModalBody>
        <ModalFooter>
          <Button variant="warning" icon={<UserIcon />} onClick={handleConfirmImpersonate}>
            Impersonate
          </Button>
          <Button variant="link" onClick={() => setConfirmTarget(null)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export { Customers };
